// Gatherings, "Dinner call" (M71, docs/gatherings.md, D282–D289). The title,
// place and note are sealed like a poll's words; replies, arrivals and rings
// are metadata the server keeps and broadcasts as message:gathering. These
// are the pure parts the card and composer build on.
import {
  GATHERING_LIMITS,
  type GatheringReply,
  type GatheringReplyRow,
  type GatheringState,
} from '@twinden/shared';
import type { ChatMessage } from '../components/chat/messageUtils';
import { t, type MessageKey } from '../i18n';
import { ApiError } from './api';
import { appLocale, formatClock } from './format';

/** Rings are on by default in small chats, off in bigger ones (D286). */
export function defaultRings(participantCount: number): boolean {
  return participantCount <= GATHERING_LIMITS.RING_DEFAULT_MAX_PEOPLE;
}

/** Your answer, if you've given one. */
export function myGatheringReply(
  state: GatheringState,
  userId: string | undefined
): GatheringReplyRow | undefined {
  if (!userId) return undefined;
  return state.replies.find((row) => row.userId === userId);
}

/**
 * The gathering after you answer (a new answer replaces the old, D284), or
 * take your answer back with null: what the card shows before the server
 * answers. As on the server, an arrival stays unless you say Not today, and
 * a changed answer keeps its place in the list.
 */
export function withMyReply(
  state: GatheringState,
  userId: string,
  reply: GatheringReply | null,
  lateMinutes?: number
): GatheringState {
  if (reply === null) {
    return {
      ...state,
      replies: state.replies.filter((row) => row.userId !== userId),
    };
  }
  const earlier = myGatheringReply(state, userId);
  const row: GatheringReplyRow = {
    userId,
    reply,
    lateMinutes: reply === 'late' ? (lateMinutes ?? null) : null,
    arrivedAt: reply === 'not' ? null : (earlier?.arrivedAt ?? null),
  };
  return {
    ...state,
    replies: earlier
      ? state.replies.map((r) => (r.userId === userId ? row : r))
      : [...state.replies, row],
  };
}

/** A message:gathering event applied to a chat's messages. */
export function applyGatheringEvent(
  list: ChatMessage[],
  event: { messageId: string; gathering: GatheringState }
): ChatMessage[] {
  return list.map((message) =>
    message.id === event.messageId
      ? { ...message, gathering: event.gathering }
      : message
  );
}

const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

/**
 * "Today, 8:30 pm", "Tomorrow, 1:00 pm" or "Sun 8 Nov, 1:00 pm", in the
 * app's Indian dates and times by default (D145).
 */
export function formatGatheringTime(
  startsAt: string,
  now: number = Date.now(),
  locale: string | undefined = appLocale()
): string {
  const when = new Date(startsAt);
  const today = new Date(now);
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const time = formatClock(when, locale);
  if (sameDay(when, today)) return t('chat.gathering.today', { time });
  if (sameDay(when, tomorrow)) return t('chat.gathering.tomorrow', { time });
  // "Sun 8 Nov": the parts without the comma some locales put in.
  const date = new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
    .formatToParts(when)
    .filter((part) => part.type !== 'literal')
    .map((part) => part.value)
    .join(' ');
  // Joined like the catalogs' "Today, {time}"; a comma is the same in
  // every language the app has, so it isn't a catalog string.
  return `${date}, ${time}`;
}

const ERROR_KEYS: Record<string, MessageKey> = {
  GATHERING_ENDED: 'chat.gathering.errEnded',
  GATHERING_LIMIT: 'chat.gathering.errLimit',
  GATHERING_TOO_SOON: 'chat.gathering.errTooSoon',
  GATHERING_RINGS_OFF: 'chat.gathering.errRingsOff',
  GATHERING_CANCEL_NOT_ALLOWED: 'chat.gathering.errCancelNotAllowed',
  GATHERING_RING_NOT_ALLOWED: 'chat.gathering.errRingNotAllowed',
  GATHERING_IN_PAST: 'chat.gathering.errInPast',
  GATHERING_TOO_FAR: 'chat.gathering.errTooFar',
};

/**
 * What a refused gathering action says, in the app's language: the server's
 * words are English, so the card words each by its error code. Anything else
 * keeps the usual toast text.
 */
export function gatheringErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const key = ERROR_KEYS[err.code];
    return key ? t(key) : err.message;
  }
  return t('common.somethingWrong');
}
